import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { Alert, Chip, useTheme } from '@mui/material';
import clsx from 'clsx';
import { LoadingSkeleton } from 'components/LoadingSkeleton';
import { useDimension } from 'kineticUtils/hooks';
import React, { MouseEventHandler, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeTree } from 'react-vtree';
import { ListChildComponentProps } from 'react-window';
import useTreeMenuStyles from './styles';
import { ItemProps, TreeMenuItem, TreeMenuProps } from './types';

const useRowColors = () => {
  const { palette } = useTheme();

  const getColors = useCallback(
    (data: { color?: string; group?: boolean }) => {
      if (!data?.color) {
        return { backgroundColor: 'transparent' };
      }
      return {
        backgroundColor: data?.color,
        color: palette.getContrastText(data?.color),
      };
    },
    [palette]
  );

  return { getColors };
};

export type StackItem = { nestingLevel: number; node: TreeMenuItem };
const EXPAND_BUTTON_WIDTH_PX = 24;
const NESTING_MARGIN_FACTOR = 7;
export const ESTIMATED_ITEM_SIZE = 32;

/**
 * vtree cannot get a height by ID, only index.
 * patch to store and set height by ID
 */
export const RowComponent = (props: ListChildComponentProps<any>) => {
  const { style, data, index } = props;

  const itemData = data.treeData;
  const id = data.order[index];
  const itemProps = data.records[id];
  const Component = data.component;
  return (
    <div style={style}>
      <Component {...itemProps} itemData={itemData} />
    </div>
  );
};

const Item = (props: ItemProps) => {
  const {
    isOpen,
    style,
    toggle,
    resize,
    data: {
      isLeaf,
      nestingLevel,
      node: { data, id, label, to, extraContent },
    },
    itemData,
  } = props;

  const { getColors } = useRowColors();
  const { color, disabled } = data ?? {};
  const classes = useTreeMenuStyles();

  const contentRef = useRef(null);
  const { height } = useDimension(contentRef, { includePadding: true });

  useEffect(
    () => {
      if (height !== 0 && height !== ESTIMATED_ITEM_SIZE) {
        resize(height, true);
      }
    },
    // don't use `resize` as a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [height]
  );
  const labelStyles = getColors(data);
  const labelElement = color ? (
    <Chip classes={{ root: classes.chipRoot }} label={label} style={labelStyles} size='small' />
  ) : (
    label
  );

  const labelNode = to ? (
    <Link
      id={`treeMenuLink-${id}`}
      to={to}
      className={classes.link}
      style={{ paddingLeft: isLeaf ? 10 : 0 }}
    >
      {labelElement}
    </Link>
  ) : (
    labelElement
  );

  const nestingMargin =
    nestingLevel === 0
      ? 0
      : nestingLevel * NESTING_MARGIN_FACTOR + nestingLevel * EXPAND_BUTTON_WIDTH_PX;

  const onExpandClick: MouseEventHandler<unknown> = (e) => {
    e.stopPropagation();
    toggle();
    itemData?.onNodeToggle?.(e, id);
  };

  return (
    <div
      style={{
        ...style,
        marginLeft: nestingMargin,
        width: `calc(100% - ${nestingMargin}px)`,
      }}
      className={clsx(classes.itemWrapper, { [classes.highlighted]: itemData.isSelected(id) })}
      ref={contentRef}
    >
      <div
        className={clsx(classes.itemContent, {
          [classes.disabledItemContent]: disabled,
        })}
        onClick={(e) => itemData?.onNodeSelect?.(e, id)}
      >
        {!isLeaf &&
          (isOpen ? (
            <ExpandMore className={classes.expandIcon} role='button' onClick={onExpandClick} />
          ) : (
            <ChevronRight className={classes.expandIcon} role='button' onClick={onExpandClick} />
          ))}
        <span className={classes.labelWrapper} style={to ? {} : { paddingLeft: isLeaf ? 10 : 0 }}>
          {labelNode}
        </span>
        {extraContent}
      </div>
    </div>
  );
};

/** TODO: Refactor to Composition pattern */
const TreeMenu: React.FunctionComponent<TreeMenuProps> = (props) => {
  const { menu, onNodeToggle, onNodeSelect, selected, loading, itemSize } = props;
  const { t } = useTranslation('common');

  const isSelected = (id: string) => {
    if (!selected) {
      return false;
    }
    if (typeof selected === 'string') {
      return selected === id;
    }
    return selected.includes(id);
  };

  const itemData = {
    onNodeToggle,
    onNodeSelect,
    isSelected,
  };

  const treeWalker = useCallback(
    function* (refresh: boolean) {
      const stack: StackItem[] = [];
      // we need to reverse the root in order to maintain the correct order in the stack
      const reversedRoot = [...menu].reverse().map((rootItem) => ({
        nestingLevel: 0,
        node: rootItem,
      }));

      stack.push(...reversedRoot);

      while (stack.length !== 0) {
        const stackItem = stack.pop()!;
        const { children = [], expanded = false } = stackItem.node;

        const isOpened = yield refresh
          ? {
              defaultHeight: ESTIMATED_ITEM_SIZE,
              id: stackItem.node.id,
              isLeaf: !children.length,
              isOpenByDefault: expanded,
              ...stackItem,
            }
          : stackItem.node.id;

        if (children.length && isOpened) {
          for (let i = children.length - 1; i >= 0; i--) {
            stack.push({
              nestingLevel: stackItem.nestingLevel + 1,
              node: children[i],
            });
          }
        }
      }
    },
    [menu]
  );

  if (loading) {
    return <LoadingSkeleton items={6} />;
  }

  const fixedSizeTree = itemSize ? () => itemSize : undefined;

  return (
    <AutoSizer disableWidth>
      {({ height }) => {
        return menu.length ? (
          <VariableSizeTree
            height={height}
            width='100%'
            treeWalker={treeWalker}
            overscanCount={20}
            rowComponent={RowComponent}
            itemData={itemData}
            itemSize={fixedSizeTree}
          >
            {Item}
          </VariableSizeTree>
        ) : (
          <Alert severity='info'>{t('empty_message_short', { structure: t('menu') })}</Alert>
        );
      }}
    </AutoSizer>
  );
};

export default TreeMenu;
